import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Card from '../Card';
import InputField from '../../../../components/Form/InputField';
import { colors } from '../../../../colors';
import LoadingIcons from 'react-loading-icons';
import {
  useAddExchangeConnectionMutation,
  useCheckExchangeConnectionMutation,
} from '../../../../store/services/api';
import DropdownComponent from '../../../../components/Form/Dropdown';
import { Exchange } from '../../../../types/exchange';
import Instructions from './Instructions';

const exchangeOptions = Object.values(Exchange);

interface Props {
  isActive: boolean;
  userId: number | null;
  moveToStepThree: () => void;
}

const ExchangeOnboarding = forwardRef(
  (
    { isActive, userId, moveToStepThree }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [testExchangeConnection] = useCheckExchangeConnectionMutation();
    const [addExchangeConnection] = useAddExchangeConnectionMutation();

    const [exchange, setExchange] = useState<Exchange | undefined>(
      exchangeOptions[0],
    );
    const [key, setKey] = useState<string | undefined>();
    const [secret, setSecret] = useState<string | undefined>();
    const [passphrase, setPassphrase] = useState<string | undefined>();

    const [
      isCheckExchangeConnectionLoading,
      setIsCheckExchangeConnectionLoading,
    ] = useState(false);
    const [checkExchangeConnectionError, setCheckExchangeConnectionError] =
      useState<string | undefined>();
    const [testConnectionResult, setTestConnectionResult] = useState<
      boolean | undefined
    >();

    const [
      isExchangeConnectionCreationLoading,
      setIsExchangeConnectionCreationLoading,
    ] = useState(false);
    const [
      exchangeConnectionCreationError,
      setExchangeConnectionCreationError,
    ] = useState<string | undefined>();

    useEffect(() => {
      setKey('');
      setSecret('');
      setPassphrase('');
      setCheckExchangeConnectionError(undefined);
      setTestConnectionResult(false);
      setExchangeConnectionCreationError(undefined);
    }, [exchange]);

    const handleKeyChange = useCallback(
      (key: string) => {
        if (testConnectionResult) {
          setTestConnectionResult(false);
        }

        setKey(key);
      },
      [testConnectionResult],
    );

    const handleSecretChange = useCallback(
      (secret: string) => {
        if (testConnectionResult) {
          setTestConnectionResult(false);
        }

        setSecret(secret);
      },
      [testConnectionResult],
    );

    const handlePasspraseChange = useCallback(
      (passphrase: string) => {
        if (testConnectionResult) {
          setTestConnectionResult(false);
        }

        setPassphrase(passphrase);
      },
      [testConnectionResult],
    );

    const keyField = useMemo(() => {
      return {
        label: 'Key',
        value: key,
        handleValueChange: handleKeyChange,
        shouldFit: true,
        isDisabled: !isActive,
      };
    }, [isActive, key, handleKeyChange]);

    const secretField = useMemo(() => {
      return {
        label: 'Secret',
        value: secret,
        handleValueChange: handleSecretChange,
        shouldFit: true,
        isDisabled: !isActive,
      };
    }, [isActive, secret, handleSecretChange]);

    const passphraseField = useMemo(() => {
      return {
        label: 'Passphrase',
        value: passphrase,
        handleValueChange: handlePasspraseChange,
        shouldFit: true,
        isSecret: true,
        isDisabled: !isActive,
      };
    }, [isActive, passphrase, handlePasspraseChange]);

    const checkConnection = useCallback(async () => {
      if (!key || !secret || !exchange || !userId) {
        return;
      }

      setIsCheckExchangeConnectionLoading(true);

      const result = await testExchangeConnection({
        exchange,
        key,
        secret,
        passphrase,
      });

      if (result.data?.errorMessage) {
        setCheckExchangeConnectionError(result.data?.errorMessage);
      } else if (result.data?.success) {
        setTestConnectionResult(result.data?.success);
        setCheckExchangeConnectionError(undefined);
      }

      setIsCheckExchangeConnectionLoading(false);
    }, [exchange, key, passphrase, secret, testExchangeConnection, userId]);

    const handleAddExchangeConnection = useCallback(async () => {
      if (!key || !secret || !exchange || !userId || !testConnectionResult) {
        return;
      }

      setIsExchangeConnectionCreationLoading(true);

      const result = await addExchangeConnection({
        userId,
        exchange,
        key,
        secret,
        passphrase,
      });

      if (result.data?.errorMessage) {
        setExchangeConnectionCreationError(result.data?.errorMessage);
      } else if (result.data?.success) {
        // Move to step two
        moveToStepThree();
      }

      setIsExchangeConnectionCreationLoading(false);
    }, [
      addExchangeConnection,
      exchange,
      key,
      moveToStepThree,
      passphrase,
      secret,
      testConnectionResult,
      userId,
    ]);

    const isTestConnectionButtonDisabled = !key || !secret || !isActive;
    const disableButton = !key || !secret || !isActive || !testConnectionResult;

    return (
      <>
        <Card
          index={2}
          title="Exchange Connection"
          isActive={isActive}
          ref={ref}
        >
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <DropdownComponent
                config={{
                  label: 'Exchanges',
                  value: exchange,
                  handleValueChange: setExchange,
                  options: Object.values(Exchange),
                  isDisabled: !isActive,
                }}
              />
              <InputField config={keyField} width={350} />
              <InputField config={secretField} width={350} />
              {exchange === Exchange.KuCoin && (
                <InputField config={passphraseField} width={350} />
              )}
              {(checkExchangeConnectionError ||
                exchangeConnectionCreationError) && (
                <p
                  style={{
                    color: colors.red,
                    margin: 0,
                    marginBottom: 0,
                    marginTop: -8,
                  }}
                >
                  {checkExchangeConnectionError ||
                    exchangeConnectionCreationError}
                </p>
              )}
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'end',
                  gap: 16,
                }}
              >
                <button
                  style={{
                    backgroundColor: isTestConnectionButtonDisabled
                      ? colors.gray
                      : colors.orange,
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingTop: 8,
                    paddingBottom: 8,
                    color: colors.white,
                    borderRadius: 4,
                    fontWeight: 800,
                    border: 0,
                    cursor: isTestConnectionButtonDisabled
                      ? 'default'
                      : 'pointer',
                  }}
                  disabled={isTestConnectionButtonDisabled}
                  onClick={checkConnection}
                >
                  {isCheckExchangeConnectionLoading ? (
                    <LoadingIcons.Grid width={15} height={15} />
                  ) : (
                    ' Test Connection'
                  )}
                </button>
                <button
                  onClick={handleAddExchangeConnection}
                  style={{
                    backgroundColor: disableButton ? colors.gray : colors.green,
                    border: `1px solid ${
                      disableButton ? colors.gray : colors.green
                    }`,
                    borderRadius: 4,
                    cursor: disableButton ? 'default' : 'pointer',
                    padding: 6,
                    paddingLeft: 12,
                    paddingRight: 12,
                    color: colors.white,
                    fontWeight: 800,
                    textWrap: 'nowrap',
                  }}
                  disabled={disableButton}
                >
                  {isExchangeConnectionCreationLoading ? (
                    <LoadingIcons.Grid width={15} height={15} />
                  ) : (
                    'Add Exchange Connection'
                  )}
                </button>
              </div>
            </div>
          </>
        </Card>
        {isActive && <Instructions exchange={exchange} />}
      </>
    );
  },
);

ExchangeOnboarding.displayName = 'ExchangeOnboarding';

export default ExchangeOnboarding;

/** Ask for exchange dropdown, key and secret
 *
 * We also need to provide steps to get this information.
 *
 * Binance:
 * Login onto binance
 * navigate to account from the top right menu
 * In the left menu, select API management
 * Click create api on the right and continue with systen generated
 * You can choose a name for this key, but for easy purposes here, we can call it Automagically
 * If prompted, verify with authenticator app to continue
 * You will see the information of the newly created credentials, but first we must change some things.
 * Click on edit rstrictions
 * Make sure Enable reading and Enable Spot & Margin Trading are the only two that are selected.
 * Next, select Restrict access to trusted IPs only and input the following ip address: 161.97.64.122, and click confirm
 * Press save and verify once again
 * Now we are ready to copy both the key and the secret to form on the left.
 * Caution, once you leave the api management page on binance, you will no longer be able to see the secret.
 */
